var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isPossiblePhoneNumber, isValidPhoneNumber, validatePossibleNumberWithReason, ValidationResult, } from 'lib-shared-phone-numbers';
import { getHighestUserRole, USER_ROLE_ATTRIBUTES } from 'lib-shared-user-role';
import isEmail from 'validator/lib/isEmail';
import { numberFormatterE164 } from 'app/utils/telephonyHelpers';
import { UserModalFieldAlert } from './components/fieldAlert/userModalFieldAlert';
import { validatePhoneNumber as validateE164PhoneNumberApi, validateEmail as validateEmailApi, } from './userModal.api';
export const scimTooltipContent = (_jsx(_Fragment, { children: "You cannot edit this field because the information is coming from another system." }));
export const rolesTooltipContent = (_jsxs(_Fragment, { children: [_jsx("strong", { children: "End User" }), _jsx("p", { children: "The end user is a Contact in Dixa. Contacts are created automatically when a customer contacts you by phone, chat or email. A Contact can also be added manually." }), _jsx("strong", { children: "Agent" }), _jsx("p", { children: "An agent is the default staff role in Dixa. Agents can be assigned to one or more queues and have global access to Search, Contacts, Team Presence, and Transfer Capabilities. They do not have access to Analytics or System Settings. They can only see their queues in the Dashboard." }), _jsx("strong", { children: "Team Lead" }), _jsx("p", { children: "A team lead is a people manager and can adjust agent assignments based on data. A team lead retains all the rights of an agent, but also has access to Analytics and can manage the agents and teams assigned to queues." }), _jsx("strong", { children: "System Administrator" }), _jsx("p", { children: "A system admin is a platform manager and can help keep things running smoothly in Dixa. They retain all the rights of a team lead, but can also add or demote agents, edit agent workload settings, edit queues, manage templates, manage tags, and edit flows." }), _jsx("strong", { children: "Administrator" }), _jsx("p", { children: "An administrator is a global admin or owner in Dixa. They have complete access to the platform including all the rights of a system admin. Additionally, they can manage organization and billing info, create and deactivate queues, create new channels and flows, and manage integration settings." })] }));
export const asyncValidationFieldNameMap = {
    emails: 'Email',
    phone_numbers: 'Phone number',
};
function validatePhoneNumberApi(phoneNumber) {
    return validateE164PhoneNumberApi(
    // Replace with e164 format.
    numberFormatterE164({
        phoneNumber: phoneNumber.replace(/\s/g, ''),
    }));
}
const asyncValidationApiMap = {
    emails: validateEmailApi,
    phone_numbers: validatePhoneNumberApi,
};
function getPhoneNumberValidationError(phoneNumber) {
    if (phoneNumber.trim() !== '' ||
        isPossiblePhoneNumber(phoneNumber) === false ||
        isValidPhoneNumber(phoneNumber) === false) {
        switch (validatePossibleNumberWithReason(phoneNumber)) {
            case ValidationResult.INVALID_COUNTRY_CODE:
                return `Invalid country code`;
            case ValidationResult.TOO_SHORT:
                return `Too short`;
            case ValidationResult.TOO_LONG:
                return `Too long`;
        }
    }
}
function getRequiredOrMinimumLengthError(value, minLength = 2) {
    if (!(value === null || value === void 0 ? void 0 : value.trim())) {
        return 'Required';
    }
    if (value.trim().length < minLength) {
        return 'Must be 2 characters or more';
    }
}
export const validate = (_a) => {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var { roleOptions } = _a, values = __rest(_a, ["roleOptions"]);
    values.emails = (_b = values.emails) !== null && _b !== void 0 ? _b : [];
    values.phone_numbers = (_c = values.phone_numbers) !== null && _c !== void 0 ? _c : [];
    const { isAllowedToChangeRole } = roleOptions || {};
    const errors = {};
    errors.display_name = getRequiredOrMinimumLengthError(values.display_name);
    const primaryEmailIdx = (_d = values.primary_email_idx) !== null && _d !== void 0 ? _d : 0;
    const primaryPhoneIdx = (_e = values.primary_phone_idx) !== null && _e !== void 0 ? _e : 0;
    const primaryEmail = (_g = (_f = values.emails[primaryEmailIdx]) === null || _f === void 0 ? void 0 : _f.value) === null || _g === void 0 ? void 0 : _g.trim();
    const primaryPhone = (_j = (_h = values.phone_numbers[primaryPhoneIdx]) === null || _h === void 0 ? void 0 : _h.value) === null || _j === void 0 ? void 0 : _j.trim();
    errors.emails = Array(values.emails.length).fill({});
    errors.phone_numbers = Array(values.phone_numbers.length).fill({});
    if (!values.role || values.role === 'enduser') {
        if (!primaryEmail && !primaryPhone) {
            const value = 'Either email or phone number is required';
            errors.emails[primaryEmailIdx] = { value };
            errors.phone_numbers[primaryPhoneIdx] = { value };
        }
    }
    else {
        errors.given_name = getRequiredOrMinimumLengthError(values.given_name);
        errors.family_name = getRequiredOrMinimumLengthError(values.family_name);
        if (!primaryEmail) {
            errors.emails[primaryEmailIdx] = { value: 'Required' };
        }
    }
    if (isAllowedToChangeRole && !values.role) {
        errors.role = 'Required';
    }
    validateContactPoints(values.emails, errors.emails, primaryEmailIdx, 'emails');
    validateContactPoints(values.phone_numbers, errors.phone_numbers, primaryPhoneIdx, 'phone_numbers');
    return errors;
};
const isValidContactPoint = (value, type) => {
    if (!value)
        return '';
    if (type === 'emails') {
        return !isEmail(value) ? `Invalid email` : '';
    }
    return getPhoneNumberValidationError(value);
};
function validateContactPoints(values, errors, primaryIdx, type) {
    var _a;
    const hash = {};
    for (let i = 0; i < values.length; i++) {
        const value = values[i].value;
        const error = 
        // If already has error - use it and skip other checks
        errors[i].value ||
            // If non-empty and not a valid contact point
            isValidContactPoint(value, type) ||
            // If same contact point was already entered in previous fields
            (value && hash[value] ? 'Cannot be a duplicate' : '');
        // Handle case for endusers if email OR phone number is defined (so the other one is allowed to be empty),
        // and only additional email/phone number was added without primary, for the other contact point.
        // Additional only (without primary) emails / phone numbers are not allowed.
        if (i !== primaryIdx &&
            !values[primaryIdx].value &&
            value &&
            !((_a = errors[primaryIdx]) === null || _a === void 0 ? void 0 : _a.value)) {
            errors[primaryIdx] = {
                value: `Primary ${'emails' ? 'email' : 'phone number'} should be defined`,
            };
        }
        // If not empty preserve value in hash to check for duplicates
        if (value)
            hash[value] = true;
        errors[i] = { value: error };
    }
}
let asyncValidateTimeout = null;
const asyncValidateDelay = 1500; // Ajax validation doesn't need to be that quick
export const asyncValidate = (values) => {
    if (asyncValidateTimeout) {
        clearTimeout(asyncValidateTimeout);
    }
    return new Promise((resolve, reject) => {
        asyncValidateTimeout = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            const errorResponses = yield Promise.all(Object.keys(asyncValidationFieldNameMap).flatMap((k) => {
                var _a;
                const key = k;
                return ((_a = values[key]) !== null && _a !== void 0 ? _a : []).map(({ value }) => {
                    if (!value) {
                        return Promise.resolve({ data: null, key });
                    }
                    // Do not send additional requests if sync validation fails.
                    // redux-form seem to run async validation before sync.
                    // See for details - https://github.com/redux-form/redux-form/issues/3944
                    const error = isValidContactPoint(value, key);
                    if (error) {
                        return Promise.resolve({ data: null, key });
                    }
                    return asyncValidationApiMap[key](value).then(({ data }) => ({
                        data,
                        key,
                    }));
                });
            }));
            const errors = errorResponses.reduce((errors, { data, key }) => {
                var _a;
                errors[key] = (_a = errors[key]) !== null && _a !== void 0 ? _a : [];
                // Set null to preserve order in array, with null being 'no error for this particular element in array'.
                const maybeError = data && !data.available && data.conflicting_id !== values.id
                    ? {
                        value: (_jsx(UserModalFieldAlert, { field: asyncValidationFieldNameMap[key], userId: data.conflicting_id })),
                    }
                    : null;
                errors[key].push(maybeError);
                errors.hasError = errors.hasError || !!maybeError;
                return errors;
            }, { hasError: false });
            if (errors.hasError) {
                return reject(errors);
            }
            return resolve();
        }), asyncValidateDelay);
    });
};
export const buildNewUser = ({ values, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const roles = values.role ? USER_ROLE_ATTRIBUTES[values.role].roles : [];
    const primaryEmailIdx = (_a = values.primary_email_idx) !== null && _a !== void 0 ? _a : 0;
    const primaryPhoneIdx = (_b = values.primary_phone_idx) !== null && _b !== void 0 ? _b : 0;
    const newUser = {
        name: values.display_name,
        roles,
        channels: ['email', 'pstn_phone', 'widget_chat'],
    };
    if (typeof values.given_name === 'string' && ((_c = values.given_name) === null || _c === void 0 ? void 0 : _c.trim())) {
        newUser.given_name = values.given_name;
    }
    if (typeof values.family_name === 'string' && ((_d = values.family_name) === null || _d === void 0 ? void 0 : _d.trim())) {
        newUser.family_name = values.family_name;
    }
    const email = (_f = (_e = values.emails) === null || _e === void 0 ? void 0 : _e[primaryEmailIdx].value) === null || _f === void 0 ? void 0 : _f.trim();
    if (email && typeof email === 'string') {
        newUser.email = email;
    }
    const phoneNumber = (_h = (_g = values.phone_numbers) === null || _g === void 0 ? void 0 : _g[primaryPhoneIdx].value) === null || _h === void 0 ? void 0 : _h.trim();
    if (phoneNumber && typeof phoneNumber === 'string') {
        // Use E.164 format.
        newUser.phone_number = numberFormatterE164({ phoneNumber });
    }
    if (values.company_id &&
        typeof values.company_id === 'string' &&
        values.company_id.trim() !== '') {
        newUser.company_id = values.company_id;
    }
    if (values.avatar_url) {
        newUser.avatar_url = values.avatar_url;
    }
    const additionalEmails = (_j = values.emails) === null || _j === void 0 ? void 0 : _j.map(({ value }, i) => (i !== primaryEmailIdx ? value === null || value === void 0 ? void 0 : value.trim() : null)).filter((x) => !!x);
    if (additionalEmails === null || additionalEmails === void 0 ? void 0 : additionalEmails.length) {
        newUser.additional_emails = additionalEmails;
    }
    const additionalPhoneNumbers = (_k = values.phone_numbers) === null || _k === void 0 ? void 0 : _k.map(({ value }, i) => {
        // Omit primary phone number
        if (i === primaryPhoneIdx)
            return null;
        const phoneNumber = value === null || value === void 0 ? void 0 : value.trim();
        return phoneNumber ? numberFormatterE164({ phoneNumber }) : phoneNumber;
    }).filter((x) => !!x);
    if (additionalPhoneNumbers === null || additionalPhoneNumbers === void 0 ? void 0 : additionalPhoneNumbers.length) {
        newUser.additional_phone_numbers = additionalPhoneNumbers;
    }
    return newUser;
};
export const buildUpdateUser = ({ values, user, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    const roles = values.role ? USER_ROLE_ATTRIBUTES[values.role].roles : [];
    const primaryEmailIdx = (_a = values.primary_email_idx) !== null && _a !== void 0 ? _a : 0;
    const primaryPhoneIdx = (_b = values.primary_phone_idx) !== null && _b !== void 0 ? _b : 0;
    const email = (_d = (_c = values.emails) === null || _c === void 0 ? void 0 : _c[primaryEmailIdx].value) === null || _d === void 0 ? void 0 : _d.trim();
    const phoneNumber = (_f = (_e = values.phone_numbers) === null || _e === void 0 ? void 0 : _e[primaryPhoneIdx].value) === null || _f === void 0 ? void 0 : _f.trim();
    const patch = {
        updates: {
            email,
            display_name: values.display_name,
            phone_number: phoneNumber ? numberFormatterE164({ phoneNumber }) : '',
            company_id: values.company_id,
            roles,
            additional_emails: (_g = values.emails) === null || _g === void 0 ? void 0 : _g.map(({ value }, i) => (i !== primaryEmailIdx ? value === null || value === void 0 ? void 0 : value.trim() : null)).filter((x) => !!x),
            additional_phone_numbers: (_h = values.phone_numbers) === null || _h === void 0 ? void 0 : _h.map(({ value }, i) => {
                // Omit primary phone number
                if (i === primaryPhoneIdx)
                    return null;
                const phoneNumber = value === null || value === void 0 ? void 0 : value.trim();
                return phoneNumber
                    ? numberFormatterE164({ phoneNumber })
                    : phoneNumber;
            }).filter((x) => !!x),
        },
        deletes: [],
    };
    if (typeof values.given_name === 'string' && ((_j = values.given_name) === null || _j === void 0 ? void 0 : _j.trim())) {
        patch.updates.given_name = values.given_name;
    }
    if (typeof values.family_name === 'string' && ((_k = values.family_name) === null || _k === void 0 ? void 0 : _k.trim())) {
        patch.updates.family_name = values.family_name;
    }
    if (!patch.updates.phone_number) {
        delete patch.updates.phone_number;
        patch.deletes.push('phone_number');
    }
    if (!email) {
        delete patch.updates.email;
        patch.deletes.push('email');
    }
    if (patch.updates.company_id === null ||
        values.company_id === '' ||
        values.company_id === undefined) {
        delete patch.updates.company_id;
        patch.deletes.push('company_id');
    }
    if (patch.updates.roles === null ||
        !values.role ||
        values.roles === undefined) {
        delete patch.updates.roles;
    }
    // Remove email from patch if it is the same as on init.
    if (email === user.email) {
        delete patch.updates.email;
    }
    // Remove phone number from patch if it is the same as on init.
    if (phoneNumber === user.phone_number) {
        delete patch.updates.phone_number;
    }
    const additionalEmails = (_l = patch.updates.additional_emails) !== null && _l !== void 0 ? _l : [];
    // Remove additional_emails from patch if it is the same as on init.
    if (additionalEmails.length &&
        additionalEmails.length === ((_m = user.additional_emails) === null || _m === void 0 ? void 0 : _m.length) &&
        additionalEmails.every((e) => { var _a; return (_a = user.additional_emails) === null || _a === void 0 ? void 0 : _a.includes(e); })) {
        delete patch.updates.additional_emails;
    }
    const additionalPhoneNumbers = (_o = patch.updates.additional_phone_numbers) !== null && _o !== void 0 ? _o : [];
    // Remove additional_phone_numbers from patch if it is the same as on init.
    if (additionalPhoneNumbers.length &&
        additionalPhoneNumbers.length === ((_p = user.additional_phone_numbers) === null || _p === void 0 ? void 0 : _p.length) &&
        additionalPhoneNumbers.every((e) => { var _a; return (_a = user.additional_phone_numbers) === null || _a === void 0 ? void 0 : _a.includes(e); })) {
        delete patch.updates.additional_phone_numbers;
    }
    if (!additionalEmails.length && ((_q = user.additional_emails) === null || _q === void 0 ? void 0 : _q.length)) {
        delete patch.updates.additional_emails;
        patch.deletes.push('additional_emails');
    }
    if (!additionalPhoneNumbers.length && ((_r = user.additional_phone_numbers) === null || _r === void 0 ? void 0 : _r.length)) {
        delete patch.updates.additional_phone_numbers;
        patch.deletes.push('additional_phone_numbers');
    }
    if (((_s = user.avatar_url) === null || _s === void 0 ? void 0 : _s.trim()) && values.avatar_url === null) {
        patch.deletes.push('avatar_url');
    }
    else if (((_t = user.avatar_url) === null || _t === void 0 ? void 0 : _t.trim()) !== ((_u = values.avatar_url) === null || _u === void 0 ? void 0 : _u.trim())) {
        patch.updates.avatar_url = values.avatar_url;
    }
    return { userId: user.id, patch: patch };
};
export function getRoleOptions(roles, isAllowedToChangeRole) {
    const memberTypeOptions = [
        {
            id: 'enduser',
            name: USER_ROLE_ATTRIBUTES['enduser'].prettyName,
        },
    ];
    if (isAllowedToChangeRole) {
        memberTypeOptions.push({
            id: 'agent',
            name: USER_ROLE_ATTRIBUTES['agent'].prettyName,
        }, {
            id: 'teamlead',
            name: USER_ROLE_ATTRIBUTES['teamlead'].prettyName,
        }, {
            id: 'systemadmin',
            name: USER_ROLE_ATTRIBUTES['systemadmin'].prettyName,
        }, {
            id: 'admin',
            name: USER_ROLE_ATTRIBUTES['admin'].prettyName,
        });
    }
    const currentAgentHighestRole = getHighestUserRole(roles);
    const { privilegeLevel: currentAgentPrivilegeLevel } = USER_ROLE_ATTRIBUTES[currentAgentHighestRole];
    return currentAgentHighestRole === 'admin'
        ? memberTypeOptions
        : memberTypeOptions.filter(({ id: role }) => USER_ROLE_ATTRIBUTES[role].privilegeLevel >
            currentAgentPrivilegeLevel);
}
export function getRolesTooltipContent(isCurrentUser) {
    if (isCurrentUser) {
        return 'You can not change your own profile role.';
    }
    return rolesTooltipContent;
}
export function shouldDisableEmailField(userId, initialValues) {
    var _a, _b, _c, _d;
    if (userId === null) {
        return false;
    }
    if (((_a = initialValues === null || initialValues === void 0 ? void 0 : initialValues.roles) === null || _a === void 0 ? void 0 : _a.includes('admin')) === false &&
        ((_b = initialValues === null || initialValues === void 0 ? void 0 : initialValues.roles) === null || _b === void 0 ? void 0 : _b.includes('agent')) === false) {
        return false;
    }
    if (((_d = (_c = initialValues.emails) === null || _c === void 0 ? void 0 : _c[0].value) === null || _d === void 0 ? void 0 : _d.trim()) === '') {
        return false;
    }
    return true;
}
export function getAvatarUser(user, formValues) {
    var _a;
    return Object.assign(Object.assign(Object.assign({}, user), formValues), { name: (_a = formValues === null || formValues === void 0 ? void 0 : formValues.display_name) !== null && _a !== void 0 ? _a : user === null || user === void 0 ? void 0 : user.name });
}
/** Checks if a payload has conflicting Id */
export function hasConflictingId(payload) {
    return !!(payload === null || payload === void 0 ? void 0 : payload.conflicting_id);
}
